import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { 
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';

const ExportDialog = ({
  open,
  onClose,
  onExport,
  data = [],
  fileName = 'export',
  columns = [],
  submitting = false,
  totalCount = null,
}) => {
  const [exportFormat, setExportFormat] = useState('csv');

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleExport = () => {
    onExport?.(exportFormat);
  };

  const recordCount = totalCount !== null ? totalCount : (data?.length || 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: borderRadius.modal,
          boxShadow: shadows.xl,
          backgroundColor: colors.backgroundWhite,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: fonts.fontSizeLg,
          fontWeight: fonts.weightBold,
          color: colors.textPrimaryDark,
          borderBottom: `1px solid ${colors.borderLight}`,
          pb: spacing.md,
          pt: spacing.md,
          px: spacing.md,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: colors.primaryGradient,
              boxShadow: `0 2px 8px ${colors.primaryShadow}`,
            }}
          >
            <FileDownloadIcon sx={{ fontSize: '22px', color: colors.textWhite }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: fonts.weightBold, fontSize: '18px', color: colors.textPrimaryDark }}>
            Export Data
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
              color: colors.textPrimaryDark,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          pt: spacing.md,
          pb: spacing.sm,
          px: spacing.md,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.md,
            mt: spacing.sm,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: fonts.weightSemiBold,
              color: colors.textPrimaryDark,
              fontSize: fonts.fontSizeSm,
            }}
          >
            Select export format
          </Typography>
          <Box
            sx={{
              border: `1px solid ${colors.borderDefault}`,
              borderRadius: borderRadius.input,
              backgroundColor: colors.backgroundWhite,
              px: spacing.md,
              py: spacing.md,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            }}
          >
            <RadioGroup
              value={exportFormat}
              onChange={(event) => {
                setExportFormat(event.target.value);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.md,
              }}
            >
              <FormControlLabel
                value="csv"
                control={
                  <Radio
                    sx={{
                      color: colors.textSecondary,
                      '&.Mui-checked': {
                        color: colors.primary,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: spacing.xs }}>
                    <Typography
                      sx={{
                        fontSize: fonts.fontSizeSm,
                        fontWeight: fonts.weightSemiBold,
                        color: colors.textPrimaryDark,
                        mb: '4px',
                      }}
                    >
                      CSV (Comma Separated Values)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textSecondary,
                        fontSize: fonts.fontSizeXs,
                        lineHeight: 1.5,
                      }}
                    >
                      Compatible with Excel, Google Sheets, and other spreadsheet applications
                    </Typography>
                  </Box>
                }
                sx={(theme) => ({
                  alignItems: 'flex-start',
                  margin: 0,
                  padding: spacing.sm,
                  borderRadius: borderRadius.input,
                  border: `1px solid ${colors.borderLight}`,
                  backgroundColor: exportFormat === 'csv' ? colors.primarySoft : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: colors.primarySoft,
                  },
                })}
              />
              <FormControlLabel
                value="excel"
                control={
                  <Radio
                    sx={{
                      color: colors.textSecondary,
                      '&.Mui-checked': {
                        color: colors.primary,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: spacing.xs }}>
                    <Typography
                      sx={{
                        fontSize: fonts.fontSizeSm,
                        fontWeight: fonts.weightSemiBold,
                        color: colors.textPrimaryDark,
                        mb: '4px',
                      }}
                    >
                      Excel (.xlsx)
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textSecondary,
                        fontSize: fonts.fontSizeXs,
                        lineHeight: 1.5,
                      }}
                    >
                      Native Excel format with formatting support
                    </Typography>
                  </Box>
                }
                sx={(theme) => ({
                  alignItems: 'flex-start',
                  margin: 0,
                  padding: spacing.sm,
                  borderRadius: borderRadius.input,
                  border: `1px solid ${colors.borderLight}`,
                  backgroundColor: exportFormat === 'excel' ? colors.primarySoft : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: colors.primarySoft,
                  },
                })}
              />
            </RadioGroup>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary,
              fontSize: fonts.fontSizeXs,
              fontStyle: 'italic',
            }}
          >
            {recordCount} {recordCount === 1 ? 'record' : 'records'} will be exported
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.borderLight}`,
          px: spacing.md,
          py: spacing.sm,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: spacing.sm,
          backgroundColor: colors.backgroundSoft,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={submitting}
          sx={{
            textTransform: 'none',
            borderRadius: borderRadius.button,
            padding: `${spacing.xs} ${spacing.md}`,
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightMedium,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={submitting || recordCount === 0}
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: colors.textWhite }} /> : <FileDownloadIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: borderRadius.button,
            padding: `${spacing.xs} ${spacing.md}`,
            fontSize: fonts.fontSizeSm,
            fontWeight: fonts.weightSemiBold,
            backgroundColor: colors.primary,
            background: `${colors.buttonPrimary} !important`,
            backgroundImage: 'none !important',
            color: `${colors.textWhite} !important`,
            boxShadow: `0 4px 12px ${colors.primaryShadow}`,
            border: 'none',
            '&:hover': {
              background: `${colors.buttonPrimaryHover} !important`,
              backgroundImage: 'none !important',
              boxShadow: `0 6px 16px ${colors.primaryShadowHover}`,
              backgroundColor: colors.primaryHover,
            },
            '&.Mui-disabled': {
              background: `${colors.backgroundSoft} !important`,
              backgroundColor: `${colors.backgroundSoft} !important`,
              color: `${colors.textSecondary} !important`,
              boxShadow: 'none',
            },
          }}
        >
          {submitting ? 'Exporting...' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;

